import { Sparkles, Clock, Smartphone } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const FutureFeaturesSection = () => {
  const features = [
    {
      title: 'Payroll Intelligence',
      description: 'Automated compensation management, compliance-ready reporting, and seamless attendance integration.',
      icon: '💰',
    },
    {
      title: 'Living Arrangements',
      description: 'Manage employee housing with lease tracking, landlord coordination, and amenities management.',
      icon: '🏢',
    },
    {
      title: 'Talent Matching AI',
      description: 'AI matches candidates to roles with fit scores, skill gap analysis, and market salary benchmarks.',
      icon: '🧠',
    },
    {
      title: 'Conversational Analytics',
      description: 'Ask questions in plain language. Get instant dashboards, reports, and predictive workforce insights.',
      icon: '🤖',
    },
    {
      title: 'Time & Attendance',
      description: 'Track work patterns: office, remote, late arrivals, early departures, and overtime; with detailed analytics.',
      icon: '⏱️',
    },
    {
      title: 'Smart Document Folders',
      description: 'Organize files with drag-drop simplicity. Share securely; a modern alternative to traditional drives.',
      icon: '📁',
    },
    {
      title: 'AI Document Insights',
      description: 'Instant summaries of any document. Extract key information without reading entire files.',
      icon: '📝',
    },
    {
      title: 'Intelligent Search',
      description: 'Search inside documents, not just names. Find exactly what you need, when you need it.',
      icon: '🔍',
    },
  ];

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black_40%,transparent_100%)] opacity-20"></div>

      <div className="relative max-w-[745px] w-full mx-auto px-4 sm:px-6 overflow-hidden">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 animate-fade-in">
          <Badge className="mb-3 bg-primary/10 text-primary border-primary/20 text-xs">
            <Sparkles className="w-2.5 h-2.5 mr-1 inline" />
            Coming Soon
          </Badge>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            The <span className="emphasize">Future of Work</span> is Here
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            We're constantly innovating. Here's what's coming next to make your workplace even more <span className="emphasize-accent font-semibold text-foreground">intelligent and employee-focused</span>.
          </p>
        </div>

        {/* Features Carousel with Auto-scroll */}
        <div className="relative">
          <div className="overflow-hidden pb-6">
            <div className="flex gap-4 sm:gap-6 animate-scroll-left">
              {/* Duplicate features for seamless infinite loop */}
              {[...features, ...features].map((feature, index) => (
                <Card
                  key={index}
                  className="w-56 sm:w-64 md:w-72 flex-shrink-0 p-5 sm:p-6 hover-lift bg-card border-2 border-border/50 hover:border-primary/30 transition-all relative animate-fade-in"
                >
                  <Badge className="absolute top-3 right-3 bg-accent/10 text-accent border-accent/20 text-[10px]">
                    <Clock className="w-2.5 h-2.5 mr-0.5" />
                    Soon
                  </Badge>
                  <div className="text-3xl sm:text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-base sm:text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>

          {/* Scroll Indicators */}
          <div className="flex justify-center mt-4 space-x-1.5">
            {[...Array(Math.ceil(features.length / 3))].map((_, index) => (
              <div
                key={index}
                className="w-1.5 h-1.5 rounded-full bg-primary/20"
              ></div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-8 sm:mt-10 text-center">
          <div className="relative inline-block">
            {/* Background layer */}
            <div className="absolute inset-0 bg-dark-section rounded-lg translate-x-0.5 translate-y-0.5"></div>
            
            {/* Main card */}
            <Card className="relative p-4 sm:p-6 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-teal-500/10 border border-white/10">
            <div className="flex items-center space-x-2.5 sm:space-x-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-sm sm:text-base font-bold mb-0.5 text-white">Did you know?</h3>
                <p className="text-[10px] sm:text-xs text-gray-300">
                  hirerkey<sup>™</sup> isn't just another piece of software—<span className="emphasize-accent font-semibold text-white">it was built with purpose.</span> In 2024–2025, we spoke with <span className="emphasize-accent font-semibold text-white">over 100 HR leaders and business heads</span> to understand how AI could genuinely make their work easier. After <span className="emphasize-accent font-semibold text-white">months of listening, testing, and refining,</span> we built a platform that finally makes sense to everyone.
                </p>
              </div>
            </div>
          </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FutureFeaturesSection;